import get from 'lodash/get';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router';
import { push } from 'react-router-redux';
import { call, put, select } from 'redux-saga/effects';

import * as FPAIActions from '../actions';
import FamilyVendorBilledError from '../components/FamilyVendorBilledError/FamilyVendorBilledError';
import * as FPAIConstants from '../constants';
import * as D from '../deps';
import * as FPAISelectors from '../selectors';
import * as FPAIServices from '../services';

export function* onInitiateFamilyPlanInviteeFlow({ payload: { familyPlanInviteToken } }) {
  if (familyPlanInviteToken) {
    yield handleFamilyPlanInviteToken({ familyPlanInviteToken });
    yield put(
      D.PaymentsActions.onUpgradeCompleted({
        action: D.getInDomain('stats.constants.StatsActions.VIEW'),
        page_view: FPAIConstants.AnalyticsConstants.FAMILY_PLAN_CHILD_SUCCESS,
      }),
    );
  } else {
    yield call(handleInvalidFamilyPlanInviteToken);
  }
}

export function* handleFamilyPlanInviteToken({ familyPlanInviteToken }) {
  yield put(
    D.PaymentsActions.selectOffer({
      tier: D.PaymentsConstants.tiers.PANDORA_FAMILY,
    }),
  );
  yield put(FPAIActions.setFamilyPlanInviteCode({ familyPlanInviteToken }));

  const isLoggedIn = yield select(D.Utils.Listener.isLoggedIn);
  const isAnonListener = yield select(D.Utils.Listener.isAnonymousListener);
  const isLoggedInListener = isLoggedIn && !isAnonListener;
  if (isLoggedInListener) {
    yield handleFamilyPlanInviteTokenForLoggedInListener(familyPlanInviteToken);
  } else {
    yield handleFamilyPlanInviteTokenForLoggedOutListener();
  }
}

export function* handleInvalidFamilyPlanInviteToken() {
  yield put(
    D.Actions.Modal.openNarrativeModal(
      <FormattedMessage {...FPAIConstants.Messages.invalidPremiumFamilyInviteBody} />,
      <FormattedMessage {...D.PaymentsConstants.Messages.dismiss} />,
      { isNoMarginBody: true, isLeftAlignedBody: true },
    ),
  );
  yield handleRedirectForInvalidFamilyPlanInvite();
}

export function* handleFamilyPlanInviteTokenForLoggedInListener(familyPlanInviteToken) {
  yield call(D.PaymentsServices.getSubscriptionInfoV2);

  const isFamilyPlanMember = yield select(D.PaymentsSelectors.productTierIsFamily);
  if (isFamilyPlanMember) {
    yield put(push(D.Utils.Route.getSubscriptionPath()));
    yield put(
      D.Actions.UseCases.openErrorToast({
        ...D.PaymentsConstants.Messages.alreadySubscribedToSamePlan,
        values: { planName: D.PaymentsConstants.tiers.PANDORA_FAMILY },
        messageName: D.PaymentsConstants.ErrorCodes.ALREADY_SUBSCRIBED,
      }),
    );
    return;
  }

  const isVendorOrPartnerBilled =
    (yield select(D.PaymentsSelectors.isVendorBilled)) ||
    (yield select(D.PaymentsSelectors.isPartnerBilled));
  if (isVendorOrPartnerBilled) {
    const familyVendorBilledErrorProps = {
      familyPlanInviteToken,
    };
    yield put(push(D.Utils.Route.getSubscriptionPath()));
    yield put(
      D.Actions.Modal.openNarrativeModal(
        <FamilyVendorBilledError {...familyVendorBilledErrorProps} />,
        <FormattedMessage {...D.PaymentsConstants.Messages.dismiss} />,
        { isNoMarginBody: true, isLeftAlignedBody: true },
      ),
    );
    return;
  }

  try {
    yield call(FPAIServices.acceptFamilyPlanInvite, {
      inviteToken: decodeURIComponent(encodeURIComponent(familyPlanInviteToken)), // clears any encoding issue we may run into
    });
  } catch (error) {
    if (
      error.errorCode === 500 &&
      error.errorType ===
        D.PaymentsConstants.ErrorCodes.FAMILY_PLAN_SUB_ACCOUNT_SWITCHING_RESTRICTION_FAILED
    ) {
      // subaccount switching restriction error
      yield put(FPAIActions.clearFamilyPlanInviteeFlow());
      yield call(handleFamilyPlanSwitchingRestriction, error);
      return;
    } else if (error.errorCode !== 400) {
      // An unexpected error occurred...
      const isMobile = yield select(D.Utils.Capabilities.isMobileDevice);
      const redirect = isMobile
        ? yield call(D.Utils.Route.getLandingPath)
        : yield call(D.Utils.Route.getNowPlayingPath);
      yield put(push(redirect));
      return;
    }
  }

  // @todo - WEBCLIENT-3421 - Eventually, the billing api response for `acceptFamilyPlanInvite` should be updated to
  // already contain the subscription info so that we don't have to make an independent call to get it.
  yield D.tryCall(D.PaymentsServices.getSubscriptionInfoV2);

  const isValidFamilyPlanInvite = yield select(FPAISelectors.getIsValidFamilyPlanInviteToken);
  yield put(FPAIActions.clearFamilyPlanInviteeFlow());

  if (!isValidFamilyPlanInvite) {
    yield call(handleInvalidFamilyPlanInviteToken);
  }
}

export function* handleFamilyPlanInviteTokenForLoggedOutListener() {
  const familyPlanInviteeLandingPath = D.Utils.Route.getFamilyPlanInviteeLandingPath();
  yield put(D.Actions.Login.setAfterLoginRedirect(familyPlanInviteeLandingPath));
  yield put(push(D.Utils.Route.getLoginPath()));
}

export function* handleRedirectForInvalidFamilyPlanInvite() {
  const isLoggedIn = yield select(D.Utils.Listener.isLoggedIn);
  const isAnonListener = yield select(D.Utils.Listener.isAnonymousListener);
  if (isLoggedIn && !isAnonListener) {
    const isMobile = yield select(D.Utils.Capabilities.isMobileDevice);
    const redirect = isMobile
      ? yield call(D.Utils.Route.getLandingPath)
      : yield call(D.Utils.Route.getNowPlayingPath);
    yield put(push(redirect));
  } else {
    yield put(push(D.Utils.Route.getLoginPath()));
  }
}

export function* handleFamilyPlanSwitchingRestriction(error) {
  const { dispatch } = D.getStore();
  const activeTier = yield select(D.PaymentsSelectors.getActiveProductGroupTier);
  const onSeeOtherPlansClick = () => {
    dispatch(D.Actions.Modal.closeModal());
    dispatch(push(D.Utils.Route.getUpgradePath()));
  };
  const showLink = yield call(
    D.PaymentsUtils.isDowngrade,
    D.PaymentsConstants.tiers.PANDORA_PREMIUM,
    activeTier,
  );
  const seeOtherPlansLink = showLink && (
    <Link onClick={onSeeOtherPlansClick} className='Modal--seeOtherPlans__link'>
      <FormattedMessage {...D.PaymentsConstants.Messages.underAgeFPSubText} />
    </Link>
  );

  if (error && error.errorMessage) {
    const messageBody = error.errorMessage + ' {seeOtherPlans}';
    const switchingRestrictionMessage = {
      id: 'switchingRestrictionMessage',
      defaultMessage: messageBody,
    };
    yield put(
      D.Actions.Modal.openUnableToJoinFamilyModal(
        <FormattedMessage
          {...switchingRestrictionMessage}
          values={{ seeOtherPlans: seeOtherPlansLink }}
        />,
      ),
    );
    yield call(handleRedirectForInvalidFamilyPlanInvite);
  }
}

export function* onFamilyChildContinueClicked() {
  yield put(
    D.PaymentsActions.onUpgradeCompleted({
      action: FPAIConstants.AnalyticsConstants.CONTINUE,
      page_view: FPAIConstants.AnalyticsConstants.FAMILY_PLAN_CHILD_SUCCESS,
    }),
  );
}
